import React, { useEffect, useState, useCallback } from 'react';
import {
  getExtraConfig,
  startExtraDeploy,
  getExtraSession,
} from '../../services/extraDeploymentsApi';

const POLL_INTERVAL_MS = 5000;

const StatusBadge = ({ status }) => {
  const classes = {
    pending: 'bg-gray-200 dark:bg-slate-700 text-gray-700 dark:text-gray-300',
    inProgress: 'bg-blue-100 text-blue-700 animate-pulse',
    succeeded: 'bg-green-100 text-green-700',
    failed: 'bg-red-100 text-red-700',
    'n/a': 'bg-gray-100 text-gray-500',
  };
  const label = {
    pending: 'Pending',
    inProgress: 'Running…',
    succeeded: 'Succeeded',
    failed: 'Failed',
    'n/a': '—',
  };
  const cls = classes[status] || classes.pending;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium ${cls}`}>
      {status === 'inProgress' && (
        <span className="inline-block w-3 h-3 border-2 border-blue-600 border-t-transparent rounded-full animate-spin" />
      )}
      {label[status] || status}
    </span>
  );
};

const overallStatusClass = (status) => {
  switch (status) {
    case 'completed':       return 'bg-green-100 dark:bg-green-900 text-green-700';
    case 'partiallyFailed': return 'bg-yellow-100 dark:bg-yellow-900 text-yellow-700';
    case 'failed':          return 'bg-red-100 dark:bg-red-900 text-red-700';
    case 'building':        return 'bg-blue-100 dark:bg-blue-900 text-blue-700 animate-pulse';
    default:                return 'bg-gray-100 dark:bg-slate-800 text-gray-600 dark:text-gray-400';
  }
};

const overallStatusLabel = (status) => {
  switch (status) {
    case 'completed':       return 'Completed';
    case 'partiallyFailed': return 'Partial';
    case 'failed':          return 'Failed';
    case 'building':        return 'Running…';
    default:                return status || '—';
  }
};

const renderBranch = (template, version) =>
  (template || '').replace('{version}', version || '<version>');

const ExtraDeployments = () => {
  const [config, setConfig] = useState([]);
  const [configLoading, setConfigLoading] = useState(true);
  const [configError, setConfigError] = useState(null);

  const [version, setVersion] = useState('2.7.6');
  const [selectedSlugs, setSelectedSlugs] = useState([]);

  const [phase, setPhase] = useState('select'); // select | progress
  const [sessionId, setSessionId] = useState(null);
  const [session, setSession] = useState(null);
  const [triggering, setTriggering] = useState(false);
  const [error, setError] = useState(null);

  const [showConfirm, setShowConfirm] = useState(false);
  const [confirmInput, setConfirmInput] = useState('');
  const CONFIRMATION_PHRASE = 'DEPLOY RELEASE';

  // Load config on mount
  useEffect(() => {
    let cancelled = false;
    setConfigLoading(true);
    getExtraConfig()
      .then((data) => {
        if (cancelled) return;
        const list = Array.isArray(data) ? data : [];
        setConfig(list);
        setSelectedSlugs(list.map((e) => e.slug));
      })
      .catch((err) => {
        if (cancelled) return;
        setConfigError(
          err.status === 403
            ? 'You do not have access to this page.'
            : err.message || 'Failed to load Extra Deployment config'
        );
      })
      .finally(() => !cancelled && setConfigLoading(false));
    return () => { cancelled = true; };
  }, []);

  // Poll session
  useEffect(() => {
    if (!sessionId || phase !== 'progress') return;
    const t = setInterval(async () => {
      try {
        const s = await getExtraSession(sessionId);
        setSession(s);
        if (
          s?.overallStatus === 'completed' ||
          s?.overallStatus === 'failed' ||
          s?.overallStatus === 'partiallyFailed'
        ) {
          setTriggering(false);
        }
      } catch {
        // ignore poll errors
      }
    }, POLL_INTERVAL_MS);
    return () => clearInterval(t);
  }, [sessionId, phase]);

  const toggleSlug = (slug) => {
    setSelectedSlugs((prev) =>
      prev.includes(slug) ? prev.filter((s) => s !== slug) : [...prev, slug]
    );
  };

  const selectAll = () => setSelectedSlugs(config.map((e) => e.slug));
  const clearAll = () => setSelectedSlugs([]);

  const versionLooksValid = /^\d+\.\d+\.\d+$/.test(version.trim());
  const canDeploy = !triggering && versionLooksValid && selectedSlugs.length > 0;

  const handleDeployClick = () => {
    setShowConfirm(true);
    setConfirmInput('');
  };

  const executeDeploy = useCallback(async () => {
    setShowConfirm(false);
    setConfirmInput('');
    setError(null);
    setTriggering(true);
    setPhase('progress');
    setSession(null);
    try {
      const { sessionId: id } = await startExtraDeploy(version.trim(), selectedSlugs);
      setSessionId(id);
      const s = await getExtraSession(id);
      setSession(s);
    } catch (err) {
      setError(err.message || 'Failed to start deployment');
      setTriggering(false);
      setPhase('select');
    }
  }, [version, selectedSlugs]);

  if (configLoading) {
    return (
      <div className="p-6 text-gray-600 dark:text-gray-400">Loading Extra Deployment config…</div>
    );
  }

  if (configError) {
    return (
      <div className="p-6">
        <div className="bg-red-50 dark:bg-red-950 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-300 rounded-lg p-4">
          {configError}
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-6xl mx-auto">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-800 dark:text-gray-200">Extra Deployment</h1>
        <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
          One-click runner for release-bound pipelines that aren't part of the regular bulk deploy
          (KingCobra app/api, Innova Offloader app/api, Silverback, Blackbird Database).
        </p>
      </div>

      <div className="bg-white dark:bg-slate-900 rounded-xl border border-gray-200 dark:border-slate-700 p-6 space-y-6">
        {phase === 'select' && (
          <>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Release version
              </label>
              <input
                type="text"
                value={version}
                onChange={(e) => setVersion(e.target.value)}
                placeholder="2.7.6"
                className="w-48 px-3 py-2 border border-gray-300 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-950 text-gray-900 dark:text-gray-100"
              />
              {!versionLooksValid && version && (
                <p className="text-xs text-amber-600 mt-1">
                  Expected format like "2.7.6"
                </p>
              )}
              <p className="text-xs text-gray-500 dark:text-slate-400 mt-1">
                Each pipeline runs on its own branch convention — see the table below.
              </p>
            </div>

            <div>
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300">
                  Pipelines to fire ({selectedSlugs.length}/{config.length})
                </h3>
                <div className="flex gap-2">
                  <button
                    type="button"
                    className="text-xs text-blue-600 hover:underline"
                    onClick={selectAll}
                  >
                    Select all
                  </button>
                  <button
                    type="button"
                    className="text-xs text-gray-500 hover:underline"
                    onClick={clearAll}
                  >
                    Clear
                  </button>
                </div>
              </div>
              <div className="border border-gray-200 dark:border-slate-700 rounded-lg overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 dark:bg-slate-800">
                    <tr>
                      <th className="px-3 py-2 text-left font-medium text-gray-600 dark:text-gray-400 w-10"></th>
                      <th className="px-3 py-2 text-left font-medium text-gray-600 dark:text-gray-400">Pipeline</th>
                      <th className="px-3 py-2 text-left font-medium text-gray-600 dark:text-gray-400">Branch</th>
                      <th className="px-3 py-2 text-left font-medium text-gray-600 dark:text-gray-400">Params</th>
                      <th className="px-3 py-2 text-left font-medium text-gray-600 dark:text-gray-400">Pipeline ID</th>
                    </tr>
                  </thead>
                  <tbody>
                    {config.map((entry) => (
                      <tr
                        key={entry.slug}
                        className="border-t border-gray-100 dark:border-slate-800 hover:bg-gray-50 dark:hover:bg-slate-800 cursor-pointer"
                        onClick={() => toggleSlug(entry.slug)}
                      >
                        <td className="px-3 py-2">
                          <input
                            type="checkbox"
                            checked={selectedSlugs.includes(entry.slug)}
                            onChange={() => toggleSlug(entry.slug)}
                            onClick={(e) => e.stopPropagation()}
                            className="w-4 h-4 accent-blue-600"
                          />
                        </td>
                        <td className="px-3 py-2 font-medium">{entry.name}</td>
                        <td className="px-3 py-2 font-mono text-xs text-gray-600 dark:text-gray-400">
                          {renderBranch(entry.branchTemplate, version)}
                        </td>
                        <td className="px-3 py-2 text-xs text-gray-500 dark:text-slate-400">
                          {entry.templateParameters && Object.keys(entry.templateParameters).length > 0
                            ? Object.entries(entry.templateParameters)
                                .map(([k, v]) => `${k}=${v}`)
                                .join(', ')
                            : '—'}
                        </td>
                        <td className="px-3 py-2 text-xs text-gray-500 dark:text-slate-400">{entry.pipelineId}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {error && (
              <div className="p-3 bg-red-50 dark:bg-red-950 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-300 rounded-lg text-sm">
                {error}
              </div>
            )}

            <button
              type="button"
              className="w-full py-3 rounded-lg font-semibold text-white bg-red-600 hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
              disabled={!canDeploy}
              onClick={handleDeployClick}
            >
              🚨 Deploy Release {versionLooksValid ? version : '(enter version)'} — {selectedSlugs.length} pipeline{selectedSlugs.length === 1 ? '' : 's'}
            </button>
          </>
        )}

        {phase === 'progress' && session && (
          <div>
            <div className="flex items-center gap-2 mb-3 flex-wrap">
              <span
                className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${overallStatusClass(
                  session.overallStatus
                )}`}
              >
                {overallStatusLabel(session.overallStatus)}
              </span>
              <span className="px-2 py-0.5 rounded text-xs font-medium bg-red-100 text-red-700">
                Release {session.branch}
              </span>
              <span className="text-xs text-gray-500 dark:text-slate-400">
                {(session.pipelines || []).length} pipelines
              </span>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-200 dark:border-slate-700">
                    <th className="text-left py-2 font-medium">Pipeline</th>
                    <th className="text-left py-2 font-medium">Status</th>
                    <th className="text-left py-2 font-medium">Run</th>
                  </tr>
                </thead>
                <tbody>
                  {(session.pipelines || []).map((p, i) => (
                    <tr key={p.slug || i} className="border-b border-gray-100 dark:border-slate-800">
                      <td className="py-2 font-medium">{p.name}</td>
                      <td className="py-2">
                        <StatusBadge status={p.buildStatus} />
                        {p.buildStatus === 'failed' && p.errorMessage && (
                          <p
                            className="text-xs text-red-500 mt-0.5 truncate max-w-[260px]"
                            title={p.errorMessage}
                          >
                            {p.errorMessage}
                          </p>
                        )}
                      </td>
                      <td className="py-2">
                        {p.buildUrl ? (
                          <a
                            href={p.buildUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-blue-600 hover:underline text-xs"
                          >
                            View ↗
                          </a>
                        ) : (
                          <span className="text-xs text-gray-400">—</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {(session.overallStatus === 'completed' ||
              session.overallStatus === 'partiallyFailed' ||
              session.overallStatus === 'failed') && (
              <button
                type="button"
                className="mt-4 px-4 py-2 bg-gray-200 dark:bg-slate-700 rounded-lg hover:bg-gray-300 dark:hover:bg-slate-600 font-medium"
                onClick={() => {
                  setPhase('select');
                  setSessionId(null);
                  setSession(null);
                }}
              >
                New Deployment
              </button>
            )}
          </div>
        )}
      </div>

      {showConfirm && (
        <div
          className="fixed inset-0 bg-black/50 flex items-center justify-center z-50"
          onClick={() => setShowConfirm(false)}
        >
          <div
            className="bg-white dark:bg-slate-900 rounded-xl p-6 max-w-md w-full mx-4 shadow-xl"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="text-lg font-bold text-gray-900 dark:text-gray-100 mb-2">
              🚨 Confirm Release Deployment
            </h3>
            <div className="mb-4 p-3 bg-red-50 dark:bg-red-950 border border-red-200 dark:border-red-800 rounded-lg text-sm text-red-800 dark:text-red-200">
              <p className="mb-2">
                <strong>About to fire {selectedSlugs.length} pipeline{selectedSlugs.length === 1 ? '' : 's'} for release {version}:</strong>
              </p>
              <ul className="list-disc list-inside space-y-0.5 text-xs">
                {config
                  .filter((e) => selectedSlugs.includes(e.slug))
                  .map((e) => (
                    <li key={e.slug}>
                      <span className="font-medium">{e.name}</span>{' '}
                      <span className="font-mono opacity-80">
                        ({renderBranch(e.branchTemplate, version)})
                      </span>
                    </li>
                  ))}
              </ul>
            </div>
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">
              Type <strong>{CONFIRMATION_PHRASE}</strong> to confirm:
            </p>
            <input
              type="text"
              className="w-full px-3 py-2 border rounded-lg mb-4 dark:bg-slate-950"
              value={confirmInput}
              onChange={(e) => setConfirmInput(e.target.value)}
              placeholder={CONFIRMATION_PHRASE}
            />
            <div className="flex gap-2 justify-end">
              <button
                type="button"
                className="px-4 py-2 bg-gray-200 dark:bg-slate-700 rounded-lg hover:bg-gray-300 dark:hover:bg-slate-600"
                onClick={() => setShowConfirm(false)}
              >
                Cancel
              </button>
              <button
                type="button"
                className="px-4 py-2 text-white rounded-lg disabled:opacity-50 bg-red-600 hover:bg-red-700"
                disabled={confirmInput !== CONFIRMATION_PHRASE}
                onClick={executeDeploy}
              >
                Confirm Deploy
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ExtraDeployments;
